// Keep the transmitted audio track stable when loading states replaces OpenAL
// sources (or the emulator's AudioContext). A live track alone isn't proof of sound.
const captureContexts = new Set();
export function resumeGameCaptureAudio() {
  captureContexts.forEach(context => { if (context.state === 'suspended') context.resume().catch(() => {}); });
}

export function createGameCapture(emulator, canvas, fps = 60) {
  const stream = canvas.captureStream(fps);
  if (!stream.getVideoTracks().length) throw new Error('No game video track');
  const Context = window.AudioContext || window.webkitAudioContext;
  let capture, destination, bridge, sourceContext, sourceDestination, timer;
  const connected = new Set();
  const disconnectHooks = new Map();
  const disconnectNode = node => {
    const original = disconnectHooks.get(node);
    if (original) { node.disconnect = original; disconnectHooks.delete(node); }
    try { node.disconnect(sourceDestination); } catch {}
    connected.delete(node);
  };
  const detach = () => {
    [...connected].forEach(disconnectNode);
    try { bridge?.disconnect(); } catch {}
    sourceDestination?.stream.getTracks().forEach(track => track.stop());
    bridge = null; sourceDestination = null;
  };
  const close = () => {
    clearInterval(timer); detach();
    stream.getTracks().forEach(track => track.stop());
    if (capture) { captureContexts.delete(capture); capture.close().catch(() => {}); }
  };
  try {
    if (Context) {
      capture = new Context({ latencyHint: 'interactive' });
      captureContexts.add(capture);
      destination = capture.createMediaStreamDestination();
      destination.stream.getAudioTracks().forEach(track => stream.addTrack(track));
      const connect = () => {
        const al = emulator.Module?.AL?.currentCtx;
        const context = al?.audioCtx;
        if (!context || context.state === 'closed') return;
        if (context !== sourceContext) {
          detach(); sourceContext = context;
          sourceDestination = context.createMediaStreamDestination();
          bridge = capture.createMediaStreamSource(sourceDestination.stream);
          bridge.connect(destination);
        }
        const gains = new Set(Object.values(al.sources || {}).map(source => source?.gain).filter(Boolean));
        [...connected].forEach(node => { if (!gains.has(node)) disconnectNode(node); });
        gains.forEach(node => {
          if (!connected.has(node)) {
            connected.add(node);
            const original = node.disconnect;
            disconnectHooks.set(node, original);
            // OpenAL clears all outputs while recycling buffers. Preserve our
            // separate capture output immediately, without periodic audio gaps.
            node.disconnect = function (...args) {
              original.apply(this, args);
              if (!args.length && connected.has(node) && sourceDestination) node.connect(sourceDestination);
            };
          }
          node.connect(sourceDestination); // Web Audio deduplicates identical connections.
        });
        resumeGameCaptureAudio();
      };
      connect(); timer = setInterval(connect, 16);
    }
    return { stream, close };
  } catch (error) { close(); throw error; }
}
